import { Venue } from '@/components/home/types'
import { Tab } from '@/components/Tab/types'
import { API_URL, BACK_API_URL, RELEASE_TAG } from '@/utils/constants'
import { newRelicLog } from '@/utils/newrelic'
import { QueryDefinition } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { QueryResultSelectorResult, createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

interface GetTabDetails {
  tab_uuid: string
  venue_uuid: string
  token: string
}

interface NewRelicLogConfig<T> {
  logType: 'pageAction' | 'setCustomAttributes'
  name: string
  getAdditionalLogData?: (data?: T) => object
}

interface RTKQueryOnQueryStarted {
  queryFulfilled: Promise<any>
  getCacheEntry: () => QueryResultSelectorResult<QueryDefinition<any, any, any, any>>
}

const logToNewRelic = async <T>(
  { logType, name, getAdditionalLogData }: NewRelicLogConfig<T>,
  { queryFulfilled, getCacheEntry }: RTKQueryOnQueryStarted
) => {
  const startTime = performance.now()
  await queryFulfilled
  const endTime = performance.now()
  const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
  const response = getCacheEntry?.()
  newRelicLog(
    {
      name,
      isSuccessful: getCacheEntry().status === 'fulfilled' ? 'true' : 'false',
      duration: Number(durationInMs),
      ...getAdditionalLogData?.(response?.data),
    },
    logType
  )
}

const retryBaseQuery = retry(
  fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json')
      headers.set('Build-Version', RELEASE_TAG)
      return headers
    },
  }),
  { maxRetries: 3 }
)

export const appApi = createApi({
  reducerPath: 'api',
  baseQuery: retryBaseQuery,
  endpoints: (builder) => ({
    getVenueDetails: builder.query<Venue, string>({
      query: (venueUuid) => `/mobile/venues?venue_uuid=${venueUuid}`,
      transformResponse: (res: Venue[]) => res[0],
      async onQueryStarted(_, { queryFulfilled, getCacheEntry }) {
        const getAdditionalLogData = (venue?: Venue) => {
          const { name, venue_uuid: venueId, msa: venueMSA } = venue ?? {}
          return {
            name,
            venueId,
            venueMSA,
          }
        }
        logToNewRelic(
          { name: 'executedVenueDetailsLoad', logType: 'pageAction', getAdditionalLogData },
          { queryFulfilled, getCacheEntry }
        )
        logToNewRelic(
          { name: 'venueVisit', logType: 'setCustomAttributes', getAdditionalLogData },
          { queryFulfilled, getCacheEntry }
        )
      },
    }),
    getTabDetails: builder.query<Tab, GetTabDetails>({
      query: ({ tab_uuid, venue_uuid, token }) => ({
        url: `/mobile/tabs/${tab_uuid}.json?venue_uuid=${venue_uuid}`,
        headers: { 'X-Client-ID': token },
      }),
      async onQueryStarted(_, { queryFulfilled, getCacheEntry }) {
        logToNewRelic({ name: 'executedOpenTabLoad', logType: 'pageAction' }, { queryFulfilled, getCacheEntry })
      },
    }),
  }),
})

export const { useLazyGetVenueDetailsQuery, useLazyGetTabDetailsQuery } = appApi
